/**
 * Onboarding a client starts here — the row that does not exist yet.
 *
 * Missing from the first build of this panel, because the six surfaces Puneet listed are all per-org and
 * none of them creates the org. Following that list literally produced a panel that could edit the
 * clients the seed had imported and onboard nobody, which is the opposite of what the list was for.
 *
 * Rendered in TWO places: on the organization list, and inside the unseeded empty state. The second one
 * matters more than it looks — telling an operator to run a seed CLI is the right first suggestion on a
 * fresh database, but making it the only one strands anyone onboarding a client that was never in the
 * env configuration to begin with.
 *
 * There is deliberately no delete. It is the one action here revert cannot undo: a delete cascades to
 * domains and grants, so putting it back means re-creating rows whose ids are gone.
 */
import { useState } from 'react';

interface NewOrgFormProps {
  /** Returns the server's error text, or null. The panel re-reads and selects the new org. */
  onCreate: (slug: string, name: string) => Promise<string | null>;
  busy: boolean;
}

/**
 * What the seed generates, so a hand-created org is indistinguishable from a seeded one afterwards:
 * lowercase, spaces and punctuation to single hyphens, no leading or trailing hyphen.
 */
function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function NewOrgForm({ onCreate, busy }: NewOrgFormProps) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const failure = await onCreate(slug.trim(), name.trim());
    setError(failure);
    if (!failure) {
      setName('');
      setSlug('');
      setSlugEdited(false);
      setOpen(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded border border-teal-700 px-2.5 py-1 text-xs font-medium text-teal-800 hover:bg-teal-50"
      >
        New client
      </button>
    );
  }

  return (
    <form data-testid="new-org" onSubmit={submit} className="space-y-2">
      <div>
        <label htmlFor="new-org-display" className="block text-xs text-gray-600">
          Display name
        </label>
        <input
          id="new-org-display"
          type="text"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            // The slug is derived until the operator touches it, then left alone. Deriving it forever
            // would silently overwrite a deliberate choice; never deriving it makes them type the same
            // thing twice.
            if (!slugEdited) setSlug(slugify(e.target.value));
          }}
          placeholder="Gaia Herbs"
          className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
        />
      </div>
      <div>
        <label htmlFor="new-org-slug" className="block text-xs text-gray-600">
          Slug
        </label>
        <input
          id="new-org-slug"
          type="text"
          value={slug}
          onChange={(e) => {
            setSlugEdited(true);
            setSlug(e.target.value);
          }}
          placeholder="gaia-herbs"
          className="w-full rounded border border-gray-300 px-2 py-1 font-mono text-sm"
        />
        <p className="mt-0.5 text-xs text-gray-500">
          Permanent, and how the seed identifies this client. Domains and grants come next.
        </p>
      </div>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={busy || !slug.trim() || !name.trim()}
          className="rounded bg-teal-700 px-3 py-1 text-xs font-semibold text-white hover:bg-teal-800 disabled:opacity-40"
        >
          Create
        </button>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setError(null);
          }}
          className="rounded border border-gray-300 px-3 py-1 text-xs text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
      {error && (
        <p role="alert" className="rounded bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
          {error}
        </p>
      )}
    </form>
  );
}

export default NewOrgForm;
